import React, { useState, useRef } from "react";
import { morseCodeTable, conductorDetectiveData } from "../electronicsKitData";
import { Sparkles, Volume2, Lightbulb, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

/**
 * Interactive STEM Widgets for the Practido Electronics Fun Kit
 * 1. Conductor Detective Interactive Testing Bench (Project 11)
 * 2. Morse Code Telegraph Synthesizer & Secret Message Encoder (Project 7)
 */

export const ConductorTesterWidget = () => {
  const [selectedItem, setSelectedItem] = useState(conductorDetectiveData[0]);
  const [userGuess, setUserGuess] = useState(null); // true = conductor, false = insulator
  const [tested, setTested] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [testedItems, setTestedItems] = useState({});

  // Kid-friendly Web Audio sound effects
  const playSound = (type) => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();
      if (ctx.state === "suspended") ctx.resume();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === "success") {
        // High cheery chime (D5 -> A5)
        osc.frequency.setValueAtTime(587.33, ctx.currentTime);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        osc.start();
        osc.stop(ctx.currentTime + 0.35);
      } else if (type === "retry") {
        // Playful gentle boing
        osc.frequency.setValueAtTime(330, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(196, ctx.currentTime + 0.25);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
        osc.start();
        osc.stop(ctx.currentTime + 0.3);
      } else if (type === "click") {
        // Tactile button click
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
        osc.start();
        osc.stop(ctx.currentTime + 0.05);
      }
    } catch (e) {}
  };

  const handleSelect = (item) => {
    playSound("click");
    setSelectedItem(item);
    setUserGuess(null);
    setTested(false);
  };

  const handleGuess = (guess) => {
    playSound("click");
    setUserGuess(guess);
  };

  const handleTest = () => {
    if (userGuess === null) return;
    setTested(true);
    const isCorrect = userGuess === selectedItem.conducts;
    if (isCorrect) {
      playSound("success");
    } else {
      playSound("retry");
    }
    if (!testedItems[selectedItem.item]) {
      setTestedItems((prev) => ({ ...prev, [selectedItem.item]: isCorrect }));
      setScore((prev) => ({
        correct: isCorrect ? prev.correct + 1 : prev.correct,
        total: prev.total + 1,
      }));
    }
  };

  const resetAll = () => {
    setTestedItems({});
    setScore({ correct: 0, total: 0 });
    setUserGuess(null);
    setTested(false);
  };

  return (
    <div className="interactive-widget conductor-widget" id="conductor-widget">
      <div className="widget-header">
        <div className="widget-badge">
          <Sparkles size={16} /> Interactive Lab • Project 11
        </div>
        <h3>Conductor Detective: Test Household Objects!</h3>
        <p>
          Guess first: does the material let electric current pass through to light the LED? Then touch the virtual probes to verify!
        </p>
      </div>

      <div className="conductor-grid">
        {/* Left: Item Picker List */}
        <div className="item-picker-col">
          <div className="picker-title">
            <span>Select an object to test:</span>
            {score.total > 0 && (
              <span className="score-pill">
                Score: {score.correct}/{score.total}
              </span>
            )}
          </div>
          <div className="items-list">
            {conductorDetectiveData.map((obj) => {
              const status = testedItems[obj.item];
              return (
                <button
                  key={obj.item}
                  onClick={() => handleSelect(obj)}
                  className={`item-btn ${selectedItem.item === obj.item ? "active" : ""} ${
                    status === true ? "correct" : status === false ? "incorrect" : ""
                  }`}
                >
                  <span className="item-name">{obj.item}</span>
                  <span className="material-tag">{obj.material}</span>
                  {status === true && <CheckCircle2 size={16} className="status-icon success" />}
                  {status === false && <XCircle size={16} className="status-icon fail" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: The Virtual Circuit Testing Stage */}
        <div className="testing-stage-col">
          <div className={`circuit-stage ${tested && selectedItem.conducts ? "circuit-active" : ""}`}>
            {/* Visual LED status */}
            <div className="stage-led-display">
              <div
                className={`led-bulb ${
                  tested && selectedItem.conducts
                    ? "glowing-red"
                    : tested && !selectedItem.conducts
                    ? "off"
                    : "standby"
                }`}
              >
                <Lightbulb size={36} />
              </div>
              <div className="led-caption">
                {tested
                  ? selectedItem.conducts
                    ? "✨ LED GLOWS! Circuit is Complete!"
                    : "❌ No Light: Circuit is Broken"
                  : "Touch probes to test circuit"}
              </div>
            </div>

            {/* Object being inspected */}
            <div className="active-object-card">
              <span className="tested-label">Testing Object:</span>
              <h4 className="active-item-title">{selectedItem.item}</h4>
              <p className="active-item-mat">Made of: <strong>{selectedItem.material}</strong></p>
            </div>

            {/* Step 1: User Guessing */}
            {!tested ? (
              <div className="guess-section">
                <span className="guess-prompt">Step 1: Make your scientific prediction:</span>
                <div className="guess-buttons">
                  <button
                    onClick={() => handleGuess(true)}
                    className={`guess-btn ${userGuess === true ? "selected" : ""}`}
                  >
                    ⚡ It's a Conductor (Will Glow)
                  </button>
                  <button
                    onClick={() => handleGuess(false)}
                    className={`guess-btn ${userGuess === false ? "selected" : ""}`}
                  >
                    🛑 It's an Insulator (No Light)
                  </button>
                </div>

                <button
                  onClick={handleTest}
                  disabled={userGuess === null}
                  className="btn test-probes-btn"
                >
                  <Sparkles size={16} /> Touch Probes to {selectedItem.item}
                </button>
              </div>
            ) : (
              <div className="result-section">
                <div className={`result-banner ${userGuess === selectedItem.conducts ? "banner-success" : "banner-retry"}`}>
                  {userGuess === selectedItem.conducts ? (
                    <>
                      <CheckCircle2 size={20} />
                      <strong>Spot on, Detective!</strong> You predicted correctly.
                    </>
                  ) : (
                    <>
                      <XCircle size={20} />
                      <strong>Good try!</strong> Science is all about testing.
                    </>
                  )}
                </div>
                <div className="science-explanation">
                  <strong>The Science: </strong>
                  {selectedItem.explanation}
                </div>
                <div className="stage-actions">
                  <button
                    onClick={() => {
                      const currentIndex = conductorDetectiveData.findIndex(
                        (i) => i.item === selectedItem.item
                      );
                      const nextItem =
                        conductorDetectiveData[(currentIndex + 1) % conductorDetectiveData.length];
                      handleSelect(nextItem);
                    }}
                    className="btn next-item-btn"
                  >
                    Test Next Object →
                  </button>
                  <button onClick={resetAll} className="btn-text-reset">
                    <RefreshCw size={14} /> Reset score
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MorseCodeWidget = () => {
  const [inputText, setInputText] = useState("PRACTIDO");
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeCodeChar, setActiveCodeChar] = useState(null);
  const [ledFlash, setLedFlash] = useState(false);
  const audioCtxRef = useRef(null);

  // Web Audio Tone Generator for 800Hz telegraph beep
  const playTone = (durationMs) => {
    return new Promise((resolve) => {
      try {
        if (!audioCtxRef.current) {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (AudioContextClass) {
            audioCtxRef.current = new AudioContextClass();
          }
        }
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
          audioCtxRef.current.resume();
        }

        if (!audioCtxRef.current) {
          setTimeout(resolve, durationMs);
          return;
        }

        const osc = audioCtxRef.current.createOscillator();
        const gain = audioCtxRef.current.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(750, audioCtxRef.current.currentTime);

        gain.gain.setValueAtTime(0.2, audioCtxRef.current.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtxRef.current.currentTime + durationMs / 1000);

        osc.connect(gain);
        gain.connect(audioCtxRef.current.destination);

        setLedFlash(true);
        osc.start();

        setTimeout(() => {
          try {
            osc.stop();
            osc.disconnect();
          } catch (e) {}
          setLedFlash(false);
          resolve();
        }, durationMs);
      } catch (e) {
        setTimeout(resolve, durationMs);
      }
    });
  };

  // Convert text to morse sequence
  const textToMorse = (text) => {
    return text
      .toUpperCase()
      .split("")
      .map((char) => {
        const found = morseCodeTable.find((m) => m.letter === char);
        return found ? found.code : " ";
      })
      .join("  ");
  };

  // Play string of dots and dashes
  const playMorseSequence = async (morseString) => {
    if (isPlaying) return;
    setIsPlaying(true);

    const DOT_TIME = 90;
    const DASH_TIME = 270;
    const SYMBOL_GAP = 90;
    const LETTER_GAP = 280;

    for (let i = 0; i < morseString.length; i++) {
      const symbol = morseString[i];
      setActiveCodeChar(i);

      if (symbol === "·" || symbol === ".") {
        await playTone(DOT_TIME);
        await new Promise((r) => setTimeout(r, SYMBOL_GAP));
      } else if (symbol === "–" || symbol === "-") {
        await playTone(DASH_TIME);
        await new Promise((r) => setTimeout(r, SYMBOL_GAP));
      } else if (symbol === " ") {
        await new Promise((r) => setTimeout(r, LETTER_GAP));
      }
    }

    setActiveCodeChar(null);
    setIsPlaying(false);
  };

  const handlePlayLetter = (letterObj) => {
    playMorseSequence(letterObj.code);
  };

  return (
    <div className="interactive-widget morse-widget" id="morse-widget">
      <div className="widget-header">
        <div className="widget-badge">
          <Volume2 size={16} /> Interactive Station • Project 7
        </div>
        <h3>Morse Code Machine: Audio & Light Telegraph</h3>
        <p>
          Click any letter or type a secret message to hear the real buzzer beep and watch the telegraph LED flash!
        </p>
      </div>

      <div className="morse-console">
        {/* Top Control Bar */}
        <div className="telegraph-status-row">
          <div className="telegraph-led-box">
            <div className={`telegraph-led ${ledFlash ? "active" : ""}`}></div>
            <span>Telegraph Lamp: {ledFlash ? "FLASHING" : "READY"}</span>
          </div>

          <div className="quick-transmits">
            <button
              onClick={() => playMorseSequence("··· ––– ···")}
              disabled={isPlaying}
              className="btn btn-sos"
            >
              🚨 Transmit SOS (··· ––– ···)
            </button>
          </div>
        </div>

        {/* Text Transmitter Input */}
        <div className="transmitter-box">
          <label htmlFor="morse-input">Type your secret message:</label>
          <div className="input-with-action">
            <input
              id="morse-input"
              type="text"
              value={inputText}
              maxLength={20}
              onChange={(e) => setInputText(e.target.value.slice(0, 20))}
              placeholder="Type word (e.g. SECRET)"
            />
            <button
              onClick={() => playMorseSequence(textToMorse(inputText))}
              disabled={isPlaying || !inputText.trim()}
              className="btn transmit-btn"
            >
              <Volume2 size={16} /> {isPlaying ? "Transmitting..." : "Send Message"}
            </button>
          </div>

          <div className="morse-translation-box">
            <span className="label">Morse Code Transmission:</span>
            <div className="code-display font-mono">
              {textToMorse(inputText) || "··· ––– ···"}
            </div>
          </div>
        </div>

        {/* A-Z Reference Pad */}
        <div className="morse-alphabet-section">
          <h4>Click any letter to hear & practice:</h4>
          <div className="alphabet-grid">
            {morseCodeTable.map((item) => (
              <button
                key={item.letter}
                onClick={() => handlePlayLetter(item)}
                disabled={isPlaying}
                className="letter-key"
                title={`Letter ${item.letter}: ${item.code}`}
              >
                <span className="letter-char">{item.letter}</span>
                <span className="letter-code">{item.code}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
